'use client';

import { useEffect, useMemo } from 'react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { CaptureError, getCaseDisplayName } from '@/shared/functions';
import { useCasesList } from '@/hooks/useCasesList';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { useCsamAccess } from '@/lib/csam-access';
import { isAdHocCase } from '@/shared/ad-hoc-evidence';
import type { FeatureFlagTypes } from '@/shared/types';
import { isWafBlockError } from '@/shared/waf-block';

type UsePrimaryNavCasesArgs = {
  userId: string;
  selectedOption: string | null;
  onOptionChange: (option: string | undefined) => void;
  featureFlags?: { [key: string]: FeatureFlagTypes | null };
};

export function usePrimaryNavCases({
  userId,
  selectedOption,
  onOptionChange,
  featureFlags,
}: UsePrimaryNavCasesArgs) {
  const t = useTimePilotT();
  const casesQuery = useCasesList();
  const personalFilesCaseId = usePersonalFilesCaseId();
  const { canSeeCsamTags } = useCsamAccess();

  const isCaseListLoading =
    casesQuery.isLoading ||
    (casesQuery.isPending && casesQuery.data === undefined && !casesQuery.isError);

  const caseNameOptions = useMemo(() => {
    const list = casesQuery.data;
    if (!list?.length) return [];
    const names = new Set<string>();
    for (const c of list) {
      const n = (c.caseName && c.caseName.trim() !== '' ? c.caseName : '').trim();
      if (n) names.add(n);
    }
    let sortedOptions = [...names].sort((a, b) => a.localeCompare(b));
    const fromPermissionService = featureFlags?.casesFromPermissionService === true;
    if (
      !fromPermissionService &&
      !!featureFlags?.restrictedCases &&
      Object.keys(featureFlags.restrictedCases).length > 0
    ) {
      const restrictedCases = featureFlags.restrictedCases as { [key: string]: string[] };
      const unrestrictedUsers = (restrictedCases['unrestrictedUsers'] as string[]) || [];
      const userIdLowerCase = userId.toLowerCase();
      sortedOptions = sortedOptions.filter((option: string) => {
        const caseKey = option.toLowerCase();
        const matchingPatterns: Array<{ users: string[]; deniesUnrestricted: boolean }> = [];
        for (const entry of Object.entries(restrictedCases).filter(
          (entry: [string, string[]]) => entry[0] !== 'unrestrictedUsers',
        )) {
          const restrictedCase = entry[0].toLowerCase();
          if (caseKey === restrictedCase || caseKey.startsWith(`${restrictedCase}/`)) {
            const lowerCaseUsers = entry[1]?.map((user: string) => user.toLowerCase()) || [];
            matchingPatterns.push({
              users: lowerCaseUsers,
              deniesUnrestricted: lowerCaseUsers.includes('no_unrestricted_access'),
            });
          }
        }
        if (matchingPatterns.length === 0) return true;
        const isExplicitlyAllowed = matchingPatterns.some((pattern) =>
          pattern.users.includes(userIdLowerCase),
        );
        if (isExplicitlyAllowed) return true;
        const isUnrestrictedUser = unrestrictedUsers.includes(userIdLowerCase);
        const anyDeniesUnrestricted = matchingPatterns.some((pattern) => pattern.deniesUnrestricted);
        if (isUnrestrictedUser && !anyDeniesUnrestricted) return true;
        return false;
      });
    }
    const personalName = sortedOptions.find((option) =>
      isAdHocCase(option, personalFilesCaseId),
    );
    if (personalName) {
      sortedOptions = [
        personalName,
        ...sortedOptions.filter((option) => option !== personalName),
      ];
    }
    return sortedOptions;
  }, [
    casesQuery.data,
    featureFlags?.restrictedCases,
    featureFlags?.casesFromPermissionService,
    personalFilesCaseId,
    userId,
  ]);

  const flaggedCaseNames = useMemo(() => {
    const names = new Set<string>();
    if (!canSeeCsamTags) return names;
    for (const c of casesQuery.data ?? []) {
      if (c.csamFlagged === true && c.caseName.trim()) names.add(c.caseName.trim());
    }
    return names;
  }, [canSeeCsamTags, casesQuery.data]);

  const caseComboboxOptions = useMemo(() => {
    if (caseNameOptions.length > 0) {
      return [
        { value: '*', label: t('nav.allCases') },
        ...caseNameOptions.map((option) => ({
          value: option,
          label: getCaseDisplayName(option),
          badge: flaggedCaseNames.has(option) ? 'CSAM' : undefined,
        })),
      ];
    }
    // A WAF block is not "no access": say so, or the user reads the empty list
    // as lost cases (TR-3920).
    if (isWafBlockError(casesQuery.error)) {
      return [{ value: '', label: 'Too many requests. Wait a few minutes, then reload.', disabled: true }];
    }
    return [{ value: '', label: t('nav.noCases'), disabled: true }];
  }, [caseNameOptions, casesQuery.error, flaggedCaseNames, t]);

  useEffect(() => {
    if (casesQuery.isError && casesQuery.error) {
      CaptureError(
        `Error fetching cases for user ${userId}: ${casesQuery.error.message}`,
        casesQuery.error,
      );
    }
  }, [casesQuery.isError, casesQuery.error, userId]);

  useEffect(() => {
    if (!caseNameOptions.length) return;
    const caseIdFromUrl = sessionStorage.getItem('caseIdFromUrl');
    if (!caseIdFromUrl || caseIdFromUrl.trim() === '') return;
    const sortedOptions = caseNameOptions;
    const decoded = (() => {
      try {
        return decodeURIComponent(caseIdFromUrl);
      } catch {
        return caseIdFromUrl;
      }
    })();
    const candidates = [caseIdFromUrl.trim(), decoded.trim()].filter(Boolean);
    const match = candidates.find((c) => sortedOptions.includes(c));
    if (match) {
      onOptionChange(match);
      sessionStorage.removeItem('caseIdFromUrl');
    } else {
      sessionStorage.removeItem('caseIdFromUrl');
    }
  }, [caseNameOptions, onOptionChange]);

  useEffect(() => {
    if (isCaseListLoading) return;
    if (!caseNameOptions.length) return;
    if (
      selectedOption &&
      selectedOption.trim() !== '' &&
      selectedOption !== '*' &&
      !caseNameOptions.includes(selectedOption)
    ) {
      onOptionChange(undefined);
    }
  }, [
    featureFlags?.restrictedCases,
    caseNameOptions,
    selectedOption,
    isCaseListLoading,
    onOptionChange,
  ]);

  return {
    isCaseListLoading,
    caseComboboxOptions,
    caseNameOptions,
    personalFilesCaseId,
  };
}

export function useCasePlaceholder(overflowLevel: number) {
  const t = useTimePilotT();
  return overflowLevel < 5 ? t('nav.casePlaceholder') : t('nav.casePlaceholderShort');
}
